import type { ReactNode } from "react";
import { Link } from "react-router-dom";

type Props = {
  id: string;
  step: number;
  title: string;
  children: ReactNode;
  features: string[];
  image: { src: string; alt: string };
  link?: { to: string; label: string };
  badge?: string;
  reverse?: boolean;
  tone?: "white" | "slate";
};

export const FeatureSection = ({
  id,
  step,
  title,
  children,
  features,
  image,
  link,
  badge,
  reverse = false,
  tone = "white",
}: Props) => (
  <section
    id={id}
    className={`print:break-inside-avoid ${tone === "slate" ? "bg-slate-50 border-y border-slate-200" : "bg-white"}`}
  >
    <div className="mx-auto max-w-6xl px-4 py-14">
      <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
        <div className={reverse ? "lg:order-2" : undefined}>
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-emerald-700 text-white flex items-center justify-center text-sm font-bold">
              {step}
            </div>
            {badge && (
              <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-900 ring-1 ring-amber-200">
                {badge}
              </span>
            )}
          </div>
          <h2 className="mt-4 text-2xl font-bold tracking-tight sm:text-3xl">{title}</h2>
          <div className="mt-4 space-y-3 text-slate-700 leading-relaxed">{children}</div>
          <ul className="mt-6 space-y-2 text-slate-700">
            {features.map((feature) => (
              <li key={feature} className="flex gap-3">
                <span className="mt-2 inline-block h-2 w-2 shrink-0 rounded-full bg-emerald-700"></span>
                <span>{feature}</span>
              </li>
            ))}
          </ul>
          {link && (
            <Link
              to={link.to}
              className="mt-8 inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-900 hover:bg-slate-50"
            >
              {link.label}
            </Link>
          )}
        </div>
        <div className={reverse ? "lg:order-1" : undefined}>
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg">
            <img src={image.src} alt={image.alt} loading="lazy" className="block w-full" />
          </div>
        </div>
      </div>
    </div>
  </section>
);
